import React, { useEffect, useRef, useState } from 'react';
import { 
  Activity, 
  Download, 
  Eye, 
  FileText, 
  ShieldAlert, 
  ShieldCheck, 
  Sparkles,
  Network,
  ListFilter,
  ArrowRight,
  Terminal,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { api, ApiResponseError } from '../services/api';
import { AgentPosture, AgentRun } from '../types';
import { MultiAgentGraph } from './MultiAgentGraph';
import { parseTraceEvidence, parseTraceExport, type TraceEvidence } from '../services/trace-evidence';

export const Observability: React.FC<{ demoMode?: boolean; setActiveTab?: (tab: string) => void }> = ({ demoMode = false, setActiveTab }) => {
  const [activeView, setActiveView] = useState<'traces' | 'graph'>('traces');
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [posture, setPosture] = useState<AgentPosture[]>([]);
  const [selected, setSelected] = useState<TraceEvidence | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [exportingPack, setExportingPack] = useState(false);
  const [isPostureCollapsed, setIsPostureCollapsed] = useState(false);
  const [isTracesCollapsed, setIsTracesCollapsed] = useState(false);
  const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState<string | null>(null);
  const [exportingEvidence, setExportingEvidence] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const mounted = useRef(false);
  const currentId = useRef<string | null>(null);
  const reads = useRef<{ list?: AbortController; trace?: AbortController; export?: AbortController; pack?: AbortController }>({});

  const clearDeniedEvidence = (failure: unknown) => {
    if (!(failure instanceof ApiResponseError) || ![401, 403].includes(failure.status)) return;
    Object.values(reads.current).forEach(controller => controller?.abort());
    reads.current = {};
    currentId.current = null; setSelectedId(null); setSelected(null);
    setRuns([]); setPosture([]); setHasLoaded(false); setLoading(false);
    setEvidenceLoading(false); setExportingEvidence(false); setExportingPack(false);
    setEvidenceError(null); setExportError(null);
    setError('Access to this evidence is no longer available. Sign in or check your workspace permissions.');
  };

  const startRead = (kind: keyof typeof reads.current) => {
    reads.current[kind]?.abort();
    const controller = new AbortController();
    reads.current[kind] = controller;
    const timer = window.setTimeout(() => controller.abort(), 12000);
    return {
      signal: controller.signal,
      current: () => mounted.current && reads.current[kind] === controller,
      finish: () => { window.clearTimeout(timer); if (reads.current[kind] === controller) delete reads.current[kind]; }
    };
  };
  const selectEvidence = async (id: string) => {
    if (currentId.current === id && reads.current.trace) return;
    if (currentId.current !== id) {
      reads.current.export?.abort(); delete reads.current.export;
      setExportingEvidence(false); setExportError(null); setSelected(null);
    }
    currentId.current = id; setSelectedId(id);
    const read = startRead('trace');
    setEvidenceLoading(true); setEvidenceError(null);
    try {
      const evidence = parseTraceEvidence(await api.getRunEvidence(id, read.signal), id);
      if (read.current() && currentId.current === id) setSelected(evidence);
    } catch (failure) {
      if (read.current()) { setEvidenceError('This trace could not be loaded. Retry to check its evidence.'); clearDeniedEvidence(failure); }
    } finally {
      if (read.current()) setEvidenceLoading(false);
      read.finish();
    }
  };
  const load = async () => {
    if (reads.current.list) return;
    const read = startRead('list');
    const selectionAtStart = currentId.current;
    setLoading(true); setError(null);
    try {
      const [runData, postureData] = await Promise.all([api.getRuns(read.signal), api.getPosture(read.signal)]);
      if (!Array.isArray(runData) || !Array.isArray(postureData) ||
        runData.some(run => !run || typeof run.id !== 'string' || !run.id || typeof run.agent_id !== 'string' || typeof run.trace_id !== 'string' || typeof run.task !== 'string') ||
        new Set(runData.map(run => run.id)).size !== runData.length ||
        postureData.some(item => !item || typeof item.id !== 'string' || !Number.isFinite(item.event_count) || !Number.isFinite(item.open_incidents) || typeof item.name !== 'string' || typeof item.posture !== 'string' || !Array.isArray(item.posture_reasons) || item.posture_reasons.some(reason => typeof reason !== 'string') || !Number.isFinite(item.intervention_rate))) throw Error('Unreadable observability data.');
      if (!read.current()) return;
      setRuns(runData); setPosture(postureData); setHasLoaded(true);
      // A refresh is not another selection: a click made while it was loading wins.
      if (currentId.current !== selectionAtStart) return;
      const id = runData.find(run => run.id === currentId.current)?.id || runData[0]?.id;
      if (id) void selectEvidence(id);
      else {
        reads.current.trace?.abort(); delete reads.current.trace;
        reads.current.export?.abort(); delete reads.current.export;
        currentId.current = null; setSelectedId(null); setSelected(null);
        setEvidenceLoading(false); setEvidenceError(null); setExportingEvidence(false); setExportError(null);
      }
    } catch (failure) {
      if (read.current()) { setError('Evidence overview could not be refreshed. Previously loaded rows may be out of date.'); clearDeniedEvidence(failure); }
    } finally {
      if (read.current()) setLoading(false);
      read.finish();
    }
  };

  useEffect(() => {
    mounted.current = true; void load();
    return () => { mounted.current = false; Object.values(reads.current).forEach(controller => controller?.abort()); reads.current = {}; };
  }, []);

  const download = (content: string, filename: string, type: string) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement('a'); link.href = url; link.download = filename;
    document.body.appendChild(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const exportEvidence = async () => {
    const id = currentId.current;
    if (!id || selected?.run.id !== id || selected.historical || evidenceLoading || evidenceError || reads.current.export) return;
    const read = startRead('export');
    setExportingEvidence(true); setExportError(null);
    try {
      const evidence = parseTraceExport(await api.exportEvidence({ run_id: id, agent_id: selected.run.agent_id }, read.signal), id, selected.run.agent_id);
      if (read.current() && currentId.current === id) download(JSON.stringify(evidence, null, 2), `agenticscale-evidence-${id}.json`, 'application/json');
    } catch (failure) {
      if (read.current()) { setExportError('No download was confirmed. You can request this trace export again. An export audit record may already exist.'); clearDeniedEvidence(failure); }
    } finally {
      if (read.current()) setExportingEvidence(false);
      read.finish();
    }
  };

  const exportCompliancePackage = async () => {
    if (reads.current.pack) return;
    const read = startRead('pack');
    setExportingPack(true);
    setError(null);
    try {
      const pack = await api.exportCompliancePack(read.signal);
      if (!read.current()) return;
      if (!pack || typeof pack.markdown_report !== 'string' || typeof pack.integrity_hash !== 'string') throw Error('Unreadable control-mapping evidence.');
      
      const date = new Date().toISOString().slice(0, 10);
      download(pack.markdown_report, `agenticscale-compliance-audit-package-${date}.md`, 'text/markdown;charset=utf-8');
      download(JSON.stringify(pack, null, 2), `agenticscale-compliance-audit-package-${date}.json`, 'application/json');
    } catch (err) {
      if (read.current()) { setError('Control-mapping download was not confirmed. An export audit record may already exist; retry only when ready.'); clearDeniedEvidence(err); }
    } finally {
      if (read.current()) setExportingPack(false);
      read.finish();
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
            Agent Observability & Audit Evidence
          </h1>
          <p className="mt-1.5 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
            Follow each agent run from task start to policy decision, tool outcome, multi-agent delegation chains, and compliance evidence.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
          <button
            type="button"
            onClick={exportCompliancePackage}
            disabled={exportingPack}
            className="inline-flex items-center gap-1.5 rounded-xl bg-brand-600 hover:bg-brand-500 text-white px-3.5 py-2 text-xs font-bold shadow-sm transition disabled:opacity-50"
          >
            {exportingPack ? <Sparkles className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
            {/* Was "Export SOC2 / NIST Audit Pack". The pack's contents were
                corrected when it was found asserting SOC 2 Type II and ISO
                42001, but the label kept promising an audit pack — the same
                claim, surviving one layer up. It exports the control mappings
                actually made, so that is what it now says. */}
            <span>{exportingPack ? 'Generating…' : 'Export control-mapping evidence'}</span>
          </button>
          <button 
            type="button" 
            onClick={load} 
            disabled={loading}
            className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            {loading ? 'Refreshing evidence…' : 'Refresh evidence'}
          </button>
        </div>
      </div>

      {/* View Switcher Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-3">
        <button
          type="button"
          onClick={() => setActiveView('traces')}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition ${
            activeView === 'traces'
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
          }`}
        >
          <ListFilter className="h-4 w-4" />
          <span>Execution Traces & Posture</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveView('graph')}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-bold transition ${
            activeView === 'graph'
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
          }`}
        >
          <Network className="h-4 w-4" />
          <span>Multi-Agent Delegation DAG</span>
        </button>
      </div>

      {demoMode && (
        <div className="rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-xs text-brand-900 dark:border-brand-500/20 dark:bg-brand-500/10 dark:text-brand-200">
          These traces are seeded examples, and nothing you do on this site is kept. Download and run it yourself to record your own.
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300">
          {error}
          <button type="button" onClick={load} disabled={loading} className="ml-2 underline font-bold">Retry evidence overview</button>
        </div>
      )}

      {activeView === 'graph' ? (
        <MultiAgentGraph />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="glass-panel rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-400">Runs</div>
              <div className="mt-2 text-3xl font-black text-slate-900 dark:text-white">{hasLoaded ? runs.length : '—'}</div>
            </div>
            <div className="glass-panel rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-400">Protected</div>
              <div className="mt-2 text-3xl font-black text-emerald-600 dark:text-emerald-400">{hasLoaded ? posture.filter((item) => item.posture === 'protected').length : '—'}</div>
            </div>
            <div className="glass-panel rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-400">At risk</div>
              <div className="mt-2 text-3xl font-black text-rose-600 dark:text-rose-400">{hasLoaded ? posture.filter((item) => item.posture === 'at_risk' || item.posture === 'quarantined').length : '—'}</div>
            </div>
            <div className="glass-panel rounded-2xl p-5">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-600 dark:text-slate-400">Intervention rate</div>
              <div className="mt-2 text-3xl font-black text-amber-600 dark:text-amber-400">{hasLoaded ? `${posture.length ? Math.round(posture.reduce((sum, item) => sum + item.intervention_rate, 0) / posture.length) : 0}%` : '—'}</div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
            <section className="glass-panel rounded-2xl p-5 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">Agent posture</h2>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700">
                    {posture.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsPostureCollapsed(!isPostureCollapsed)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                  aria-expanded={!isPostureCollapsed}
                  title={isPostureCollapsed ? 'Expand Agent Posture' : 'Collapse Agent Posture'}
                >
                  {isPostureCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                </button>
              </div>

              {!isPostureCollapsed && (
                <div className="space-y-3 animate-fade-in">
                  {posture.map((item) => (
                    <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-bold text-slate-900 dark:text-white">{item.name}</span>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${item.posture === 'protected' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300' : 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-300'}`}>
                          {item.posture.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                        {item.event_count} events · {item.intervention_rate}% intervention rate · {item.open_incidents} open incident(s)
                      </div>
                      <div className="mt-2 space-y-1 text-[11px] text-slate-600 dark:text-slate-300">
                        {item.posture_reasons.map((reason) => <div key={reason}>• {reason}</div>)}
                      </div>
                    </div>
                  ))}
                  {hasLoaded && !loading && !error && posture.length === 0 && <p className="text-xs text-slate-600 dark:text-slate-400">No agent posture records yet.</p>}
                </div>
              )}
            </section>

            <section className="glass-panel rounded-2xl p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <Activity className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                  <h2 className="text-base font-bold text-slate-900 dark:text-white">Execution traces</h2>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold border border-slate-200 dark:border-slate-700">
                    {runs.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {selected && !isTracesCollapsed && (
                    <button 
                      type="button" 
                      onClick={exportEvidence}
                      disabled={exportingEvidence || evidenceLoading || Boolean(evidenceError) || selected.historical}
                      aria-label="Export selected trace evidence"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200"
                    >
                      <Download className="h-3.5 w-3.5" />
                      <span>{exportingEvidence ? 'Exporting…' : 'Export'}</span>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setIsTracesCollapsed(!isTracesCollapsed)}
                    className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                    aria-expanded={!isTracesCollapsed}
                    title={isTracesCollapsed ? 'Expand Execution Traces' : 'Collapse Execution Traces'}
                  >
                    {isTracesCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {!isTracesCollapsed && (
                <div className="space-y-4 animate-fade-in">
                  <p className="text-xs text-slate-600 dark:text-slate-400" role="status">{loading ? 'Loading evidence overview…' : hasLoaded && !error ? `${runs.length} recent trace(s). Select a trace to inspect its timeline.` : 'Evidence overview is unavailable; use Retry evidence overview.'}</p>
                  {hasLoaded && !loading && !error && runs.length === 0 && <p className="text-xs text-slate-600 dark:text-slate-400">No execution traces recorded yet.</p>}
                  <div className="grid gap-3 sm:grid-cols-2">
                    {runs.map((run) => (
                      <button 
                        key={run.id} 
                        type="button" 
                        onClick={() => void selectEvidence(run.id)}
                        aria-pressed={selectedId === run.id}
                        className={`min-w-0 break-words flex flex-col rounded-xl border p-4 text-left transition ${selectedId === run.id ? 'border-brand-300 bg-brand-50 dark:border-brand-500/40 dark:bg-brand-950/20' : 'border-slate-200 bg-slate-50 hover:border-brand-200 dark:border-slate-800 dark:bg-slate-950/40'}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-bold text-slate-900 dark:text-white [overflow-wrap:anywhere]">{run.task || run.action_name || run.id}</div>
                            <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">{run.agent_name || run.agent_id}</div>
                          </div>
                          <Eye className="h-4 w-4 text-slate-400" />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2 text-[10px] font-semibold text-slate-600 dark:text-slate-400">
                          <span className="[overflow-wrap:anywhere]">{run.trace_id}</span>
                          {run.decision && (
                            <span className={run.decision === 'BLOCK' ? 'text-rose-600' : run.decision === 'REVIEW' ? 'text-amber-600' : 'text-emerald-600'}>
                              {run.decision}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                  {selectedId && <div role="status" className="text-xs text-slate-600 dark:text-slate-300 break-all">Selected trace: {selectedId}{evidenceLoading ? ' · Loading timeline…' : ''}</div>}
                  {evidenceError && <div role="alert" className="text-xs text-rose-700 dark:text-rose-300">{evidenceError}{selected ? ' Previously loaded timeline may be out of date.' : ''} <button type="button" className="font-bold underline" onClick={() => selectedId && void selectEvidence(selectedId)}>Retry selected trace</button></div>}
                  {exportError && <div role="alert" className="text-xs text-rose-700 dark:text-rose-300">{exportError}</div>}
                  {selected?.historical && <p className="text-xs text-slate-600 dark:text-slate-300">This historical safety event has no recorded run. Its timeline is available, but a run-scoped export is unavailable.</p>}
                  {selected && (
                    <div className="mt-5 min-w-0 [overflow-wrap:anywhere] rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40" aria-busy={evidenceLoading}>
                      <div className="flex items-center justify-between text-sm font-bold text-slate-900 dark:text-white mb-2">
                        <div className="flex items-center gap-2">
                          <ShieldAlert className="h-4 w-4 text-amber-500" />
                          <span>Evidence timeline: {selected.run.task || selected.run.action_name || selected.run.id}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsTimelineCollapsed(!isTimelineCollapsed)}
                          className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg transition"
                          title={isTimelineCollapsed ? 'Expand Timeline' : 'Collapse Timeline'}
                          aria-expanded={!isTimelineCollapsed}
                        >
                          {isTimelineCollapsed ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronUp className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      {!isTimelineCollapsed && (
                        <div className="mt-4 space-y-3 animate-fade-in">
                          {selected.events.length === 0 && <p className="text-xs text-slate-600 dark:text-slate-400">No events recorded for this trace yet.</p>}
                          {selected.events.map((event) => (
                            <div key={event.id} className="relative border-l-2 border-brand-200 pl-4 dark:border-brand-500/30">
                              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-600 dark:text-slate-400">
                                {event.event_type} · {new Date(event.timestamp).toLocaleString()}
                              </div>
                              <div className="mt-1 text-xs font-bold text-slate-900 dark:text-white">
                                {event.action_name || event.tool_name || event.event_type}
                              </div>
                              <div className="mt-1 text-[11px] text-slate-600 dark:text-slate-400">
                                {event.payload_summary || event.outcome || 'Evidence recorded.'}
                              </div>
                              {event.decision && (
                                <div className={`mt-1 text-[11px] font-bold ${event.decision === 'BLOCK' ? 'text-rose-600' : event.decision === 'REVIEW' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                  {event.decision} · {event.outcome || 'decision recorded'}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </>
      )}

      {setActiveTab && (
        <div className="glass-panel p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs">
          <div>
            <span className="font-bold text-slate-900 dark:text-white block">Ready to connect your AI agent?</span>
            <span className="text-slate-500 dark:text-slate-400">Register an application or copy the Python / TypeScript SDK code snippets.</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab('integrations')}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold transition shadow-sm"
            >
              <Network className="w-3.5 h-3.5" />
              <span>Manage API Keys</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('api')}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold transition shadow-sm"
            >
              <Terminal className="w-3.5 h-3.5" />
              <span>Explore API Docs</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
